import React, { useEffect, useState } from "react";
import { Autocomplete, Button, Grid, IconButton, List, ListItem, ListItemText, TextField, Tooltip, Typography, } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { BatchStep, BatchStepWithSort, SiteDTO, SiteID, StepIdWithSort } from "@beable/roster-backend-client-v1";
import { getStyles } from "../styles";
import { AddCircle, Delete } from "@mui/icons-material";
import SiteBatchStepService from "../../../services/siteBatchStepService";
import { SiteBatchStepsSummary } from "./SummaryDialog";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { isEqual } from "lodash";

interface BatchStepProps {
    site: SiteDTO;
}

export const SiteBatchSteps = ({
    site,
}: BatchStepProps) => {
    const theme = useTheme();
    const styles = getStyles(theme);
    const [dbSteps, setDbSteps] = useState<BatchStepWithSort[]>([]);
    const [currentSteps, setCurrentSteps] = useState<BatchStepWithSort[]>([]);
    const [selectedStep, setSelectedStep] = useState<BatchStep | null>(null);
    const [addedSteps, setAddedSteps] = useState<BatchStepWithSort[]>([]);
    const [deletedSteps, setDeletedSteps] = useState<BatchStepWithSort[]>([]);
    const [stepOptions, setStepOptions] = useState<BatchStep[]>([]);
    const [errorFindingData, setErrorFindingData] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);

    const getData = async (siteId: SiteID) => {
        const batchSteps = await SiteBatchStepService.batchStepsGet();
        const siteBatchSteps = await SiteBatchStepService.siteBatchStepsGet(siteId);
        if (!siteBatchSteps.length || !batchSteps.length) {
            setErrorFindingData(true);
        } 

        const filteredBatchSteps = batchSteps.filter(step => !siteBatchSteps.map(s => s.batchStep.id).includes(step.id));
        setStepOptions(filteredBatchSteps);
        setDbSteps(siteBatchSteps);
        setCurrentSteps(siteBatchSteps);
    }

    useEffect(() => {
        if (site.id) {
            getData(site.id as SiteID);
        }
    }, [site.id]);

    const handleStepChange = (_event: any, selectedStepValue: BatchStep | null) => {
        setSelectedStep(selectedStepValue);
    }

    const addStep = () => {
        const removeStepOption = (step: BatchStep) => {
            const updatedOptions = stepOptions.filter(opt => opt.id !== step.id);
            setStepOptions([...updatedOptions]);
        }
        if (selectedStep) {
            const sort = currentSteps.length;
            const selectedStepWithSort: BatchStepWithSort = { batchStep: selectedStep, sort }

            setAddedSteps([...addedSteps, ...[selectedStepWithSort]]);
            setCurrentSteps([...currentSteps, ...[selectedStepWithSort]]);

            removeStepOption(selectedStep);
            setSelectedStep(null);
        }
    }

    const deleteStep = (step: BatchStepWithSort) => {
        const deleteExistingStep = (step: BatchStepWithSort) => {
            const updatedExisting = currentSteps.filter(existing => existing.batchStep.id !== step.batchStep.id);

            updatedExisting.map((cstep, index) => {
                cstep.sort = index;
            })

            setCurrentSteps(updatedExisting);
        }
        if (dbSteps.find(dbStep => dbStep.batchStep.id === step.batchStep.id)) {
            setDeletedSteps([...deletedSteps, ...[step]])
        }
        setStepOptions([...stepOptions, ...[step.batchStep]])
        deleteExistingStep(step);
    }

    const saveSteps = async () => {
        const currentStepIdsWithSort = currentSteps.map(step => ({
            batchStepId: step.batchStep.id,
            sort: step.sort
        }))

        const updateResult = await SiteBatchStepService.siteBatchStepsSave(site.id as SiteID, currentStepIdsWithSort);
        if (!updateResult) {
            alert('error saving site batch steps');
        }
        handleReset();
    }

    const handleReset = () => {
        getData(site.id as SiteID);
        setSelectedStep(null);
        setAddedSteps([]);
        setDeletedSteps([]);
        setOpenDialog(false);
        setErrorFindingData(false);
    }

    function handleOnDragEnd(result: any) {
        if (!result.destination) return;
        const current = [...currentSteps];
        const reorderedCurrent = current.splice(result.source.index, 1);

        current.splice(result.destination.index, 0, reorderedCurrent[0]);
        current.map((step, index) => {
            step.sort = index
        })

        if (!isEqual(current, currentSteps)) {
            setCurrentSteps(current);
        }
    }

    const ErrorFindingData = () => {
        return (
            <Grid container spacing={1} sx={[styles.ml2, styles.mt1]}>
                <Grid
                    sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}
                    size={10}>
                    <Typography align-left='true'>
                        There was an error retrieving the data. Please try again.
                    </Typography>
                </Grid>
                <Grid sx={styles.formButtons} size={1}>
                    <Button
                        onClick={handleReset}
                        sx={styles.button}
                        color="secondary"
                        variant="contained">
                        Retry
                    </Button>
                </Grid>
            </Grid >
        );
    }
    const CurrentBatchStep = ({ step, index }: { step: BatchStepWithSort; index: number }) => {
        return (<Draggable
            key={`step-${index}`}
            draggableId={`step-${index}`}
            index={index}
        >
            {(provided, snapshot) => (
                <ListItem
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    sx={[styles.ml2, styles.hover, snapshot.isDragging && styles.siteStepBackGround]}
                    secondaryAction={
                        <IconButton
                            data-testId={`delete-${step.batchStep.name}`}
                            onClick={() => deleteStep(step)}
                            size="large"
                            edge="end"
                        >
                            <Delete fontSize="small" color="secondary" />
                        </IconButton>}
                >
                    <Tooltip title={step.batchStep.description} followCursor>
                        <ListItemText primary={step.batchStep.name} />
                    </Tooltip>
                </ListItem>
            )}
        </Draggable>
        )
    }
    return (
        <> {site.id ?
                    errorFindingData ? <ErrorFindingData /> : <>
                        <Grid
                            container
                            sx={[styles.ml2, styles.mt1]}
                        >
                            <Grid
                                sx={{ display: "flex", justifyContent: "flex-start", marginTop: '6px', marginBottom: '6px' }}
                                size={12}>
                                <Typography variant='h5' align-left='true'>
                                    {isEqual(currentSteps, dbSteps) ? 'Current Batch Steps' : 'Updated Batch Steps'}
                                </Typography>
                            </Grid>
                            <Grid size={12}>
                                <DragDropContext
                                    onDragEnd={handleOnDragEnd}>
                                    <Droppable droppableId="current-steps">
                                        {(provided, snapshot) => (
                                            <List
                                                {...provided.droppableProps}
                                                ref={provided.innerRef}
                                            >
                                                {currentSteps.map((step, index) =>
                                                    <CurrentBatchStep step={step} index={index} />
                                                )}
                                                {provided.placeholder}
                                            </List>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                            </Grid >
                            <Grid
                                container
                                spacing={1}
                                size={12}
                                sx={[{
                                    justifyContent: "left",
                                    alignItems: "center"
                                }, styles.mt2]}>
                                <Grid size={10}>
                                    <Autocomplete
                                        options={stepOptions}
                                        value={selectedStep || null}
                                        getOptionLabel={(step: BatchStep) => step.name}
                                        renderOption={(props, step: BatchStep) => {
                                            return (
                                                <Tooltip title={step.description} followCursor>
                                                    <li {...props} key={step.id}>
                                                        <div>{step.name}</div>
                                                    </li>
                                                </Tooltip>
                                            )
                                        }}
                                        isOptionEqualToValue={(step: BatchStep, value: BatchStep) => step.id === value?.id ? true : false}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Search for a batch step"
                                                size="medium"
                                                label="Add Batch Step"
                                                variant="outlined"
                                            />
                                        )}
                                        onChange={handleStepChange}
                                        size="medium"
                                        id="select-batch-step"
                                    />
                                </Grid>
                                <Grid sx={{ display: 'flex', justifyContent: 'center' }} size={1}>
                                    <IconButton
                                        onClick={addStep} size="large"
                                        disabled={!selectedStep}
                                        data-testId={'add-button'}
                                    >
                                        <AddCircle fontSize="medium" color={selectedStep ? "primary" : 'disabled'} />
                                    </IconButton>
                                </Grid>
                            </Grid>
                            <Grid sx={styles.formButtons} size={12}>
                                <Button
                                    onClick={handleReset}
                                    sx={styles.button}
                                    disabled={isEqual(currentSteps, dbSteps)}
                                    color="secondary">
                                    Reset
                                </Button>
                                <Button
                                    onClick={() => setOpenDialog(true)}
                                    color='primary'
                                    sx={styles.button}
                                    type='submit'
                                    disabled={!currentSteps.length || isEqual(currentSteps, dbSteps)}
                                    variant='contained'
                                    aria-label='save-button'
                                >
                                    Save
                                </Button>
                            </Grid>
                        </Grid >
                        <SiteBatchStepsSummary
                            open={openDialog}
                            onClose={() => setOpenDialog(false)}
                            currentSteps={currentSteps}
                            addedSteps={addedSteps}
                            deletedSteps={deletedSteps}
                            saveSteps={saveSteps} />
                    </>
                    : <Grid container spacing={1} sx={[styles.ml2, styles.mt1]}>
                        <Grid
                            sx={{ display: "flex", justifyContent: "flex-start", marginTop: '6px', marginBottom: '6px' }}
                            size={12}>
                            <Typography align-left='true'>
                                Select a site to view and update site batch steps.
                            </Typography>
                        </Grid>
                    </Grid >
                }</>
    );
};